<template>
	<SectionCard :title="$vuetify.lang.t('$vuetify.front.subsetting.orderTags')" icon="mdi-tag-multiple-outline">
		<template v-slot:actions>
			<v-btn small text color="primary" @click="addTag"><v-icon left small>mdi-plus</v-icon>Aggiungi tag</v-btn>
		</template>

		<div class="ot-hint mb-3">Etichette libere da mettere sugli ordini, con il loro colore. Trascina dalla maniglia per cambiarne l'ordine, poi salva.</div>

		<div v-if="tags.length === 0" class="ot-empty">
			<v-icon color="grey lighten-1" class="mb-1">mdi-tag-off-outline</v-icon>
			<div>Nessun tag configurato: gli ordini continuano a usare i colori predefiniti.</div>
		</div>

		<!-- stessa lista su desktop e telefono: le righe vanno a capo da sole -->
		<draggable v-else :list="tags" tag="div" class="ot-lines" :sort="true" handle=".ot-grab">
			<div v-for="(tag, index) in tags" :key="tag._id" class="ot-line">
				<v-icon small class="ot-grab" title="Trascina per cambiare l'ordine">mdi-drag-vertical</v-icon>
				<button type="button" class="ot-chip" :style="chipStyle(tag)" title="Cambia colore" @click="openColorPicker(tag)">{{ tag.label || 'Tag' }}</button>
				<v-text-field v-model="tag.label" label="Etichetta" dense outlined hide-details class="ot-label"></v-text-field>
				<button type="button" class="ot-color" title="Cambia colore" @click="openColorPicker(tag)">
					<span class="ot-swatch" :style="{ backgroundColor: tag.color }"></span>
					<span class="ot-hex">{{ tag.color }}</span>
				</button>
				<v-menu left eager>
					<template v-slot:activator="{ on, attrs }">
						<v-btn icon small color="primary" v-bind="attrs" v-on="on">
							<v-icon>mdi-dots-vertical</v-icon>
						</v-btn>
					</template>
					<v-list>
						<v-list-item link @click="openColorPicker(tag)">
							<v-list-item-title class="size9">Cambia colore</v-list-item-title>
						</v-list-item>
						<v-list-item link @click="removeTag(index)">
							<v-list-item-title class="size9 error--text">Rimuovi</v-list-item-title>
						</v-list-item>
					</v-list>
				</v-menu>
			</div>
		</draggable>

		<div class="ot-actions mt-4">
			<span v-if="modificati" class="ot-dirty"><v-icon small color="orange" class="mr-1">mdi-circle-medium</v-icon>Modifiche non salvate</span>
			<v-spacer></v-spacer>
			<v-btn depressed color="primary" @click="save"><v-icon left small>mdi-content-save-outline</v-icon>Salva configurazione</v-btn>
		</div>

		<ColoreDialog v-model="dialogColorPicker" :iniziale="color_to_edit.color" :etichetta="color_to_edit.label" :titolo="'Colore del tag «' + (color_to_edit.label || 'Tag') + '»'" @conferma="saveColorPicker" />

		<v-snackbar v-model="snackbar.snackbar" :color="snackbar.color" :multi-line="snackbar.mode === 'multi-line'" :timeout="snackbar.timeout">
			{{ snackbar.text }}
			<v-btn dark text @click="snackbar.snackbar = false"> Chiudi </v-btn>
		</v-snackbar>
	</SectionCard>
</template>

<script>
import axios from 'axios';
import { mapGetters, mapMutations, mapActions } from 'vuex';
import Draggable from 'vuedraggable';
import SectionCard from '@/components/core/SectionCard.vue';
import ColoreDialog from '@/views/setting/components/ColoreDialog.vue';
import { ObjectID } from 'bson';
import { isDarkColor } from '../../Helper';

export default {
	name: 'OrderTags',
	components: { Draggable, SectionCard, ColoreDialog },
	data() {
		return {
			// copia locale: si scrive nello store solo dopo il salvataggio andato a buon fine
			tags: [],
			color_to_edit: {
				_id: '',
				color: '',
				label: '',
			},
			// come erano all'apertura / all'ultimo salvataggio, per segnalare le modifiche in sospeso
			salvati: '[]',
			dialogColorPicker: false,
			snackbar: {
				snackbar: false,
				color: '',
				mode: '',
				timeout: 6000,
				text: '',
			},
		};
	},

	async created() {
		// l'action restituisce la cache dello store se il profilo e' gia' stato caricato
		await this.fetchSettings();
		this.tags = _.cloneDeep(_.orderBy(this.getSettings.order_tags || [], ['order'], ['asc']));
		this.salvati = this.firma(this.tags);
	},

	computed: {
		modificati() {
			return this.firma(this.tags) !== this.salvati;
		},
		...mapGetters({
			getToken: 'profile/getToken',
			getUrl: 'settings/getUrl',
			getSettings: 'settings/getSetting',
		}),
	},

	methods: {
		...mapMutations({
			setLogout: 'profile/setLogout',
			setPreload: 'settings/setPreload',
		}),
		...mapActions({
			fetchSettings: 'settings/asyncFetchData',
			setSettings: 'settings/updateData',
		}),

		// stesso stile del chip mostrato nella lista ordini
		chipStyle(tag) {
			return {
				backgroundColor: tag.color,
				color: isDarkColor(tag.color) ? '#FFFFFF' : '#000000',
			};
		},

		addTag() {
			this.tags.push({
				_id: _.toString(new ObjectID()),
				label: '',
				color: '#BDBDBD',
				order: this.tags.length,
			});
		},

		removeTag(index) {
			this.tags.splice(index, 1);
		},

		// niente handler @change qui: con la prop `:list`, vuedraggable riordina gia' da solo
		// l'array `tags` durante il drag (mutazione diretta sul riferimento bindato). Un ulteriore
		// riordino manuale nel change event (rimosso: vedi AZSELL-1194) duplicava lo spostamento
		// sull'array gia' aggiornato, risultando in una posizione finale sbagliata dopo il drop.

		// ordine, etichette e colori in una stringa: basta confrontarla per sapere se c'e' da salvare
		firma(lista) {
			return JSON.stringify(lista.map(t => [t._id, _.trim(t.label), t.color]));
		},

		openColorPicker(tag) {
			this.color_to_edit = { _id: tag._id, color: tag.color, label: tag.label };
			this.dialogColorPicker = true;
		},

		saveColorPicker(colore) {
			const check = _.find(this.tags, { _id: this.color_to_edit._id });
			if (check) {
				check.color = colore;
			}
		},

		save() {
			const empty = _.find(this.tags, tag => !_.trim(tag.label));
			if (empty) {
				this.snackbar.color = 'error';
				this.snackbar.text = 'Ogni tag deve avere una label';
				this.snackbar.snackbar = true;
				this.snackbar.mode = 'multi-line';
				this.snackbar.timeout = 5000;
				return;
			}

			this.setPreload(true);

			// l'ordine dell'array e' quello trascinato: il backend ci riassegna `order`
			const payload = this.tags.map((tag, index) => ({
				_id: tag._id,
				label: _.trim(tag.label),
				color: tag.color,
				order: index,
			}));

			let url = 'profile/order-tags';
			axios
				.post(`${this.getUrl}${url}`, payload, { headers: { Authorization: `Bearer ${this.getToken}` } })
				.then(({ data }) => {
					this.setSettings(data.data);
					this.tags = _.cloneDeep(_.orderBy(data.data.order_tags || [], ['order'], ['asc']));
					this.salvati = this.firma(this.tags);
					this.snackbar.color = 'success';
					this.snackbar.text = 'Operazione effettuata con  successo';
					this.snackbar.snackbar = true;
					this.snackbar.mode = 'multi-line';
					this.snackbar.timeout = 5000;
				})
				.catch(err => {
					if (err.response) {
						let errors = err.response.data.error;
						if (errors && !_.isArray(errors)) {
							err.message = errors;
						}

						this.snackbar.color = 'error';
						this.snackbar.text = err.message;
						this.snackbar.snackbar = true;
						this.snackbar.mode = 'multi-line';
						this.snackbar.timeout = 5000;

						if (err.response.status === 401) {
							this.setLogout();
							this.$router.push('/login');
						}
					} else {
						this.snackbar.color = 'error';
						this.snackbar.text = err.message;
						this.snackbar.snackbar = true;
					}
				})
				.finally(() => {
					// prima restava acceso se la richiesta falliva senza risposta dal server
					this.setPreload(false);
				});
		},
	},
};
</script>

<style scoped>
.ot-hint {
	font-size: 12px;
	color: #5f6b7a;
}
.ot-empty {
	text-align: center;
	color: #5f6b7a;
	font-size: 13px;
	padding: 20px 12px;
	border: 1px dashed #c9d4e2;
	border-radius: 8px;
	background: #f7f9fc;
}
.ot-lines {
	background: #fff;
	border: 1px solid #e1e7ef;
	border-radius: 8px;
	overflow: hidden;
}
.ot-line {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 8px 12px;
	padding: 8px 10px;
}
.ot-line + .ot-line {
	border-top: 1px solid #eef2f7;
}
.ot-grab {
	cursor: grab;
}
.ot-chip {
	display: inline-flex;
	align-items: center;
	height: 26px;
	min-width: 60px;
	max-width: 180px;
	padding: 0 12px;
	border-radius: 13px;
	border: 1px solid rgba(0, 0, 0, 0.12);
	font-size: 13px;
	font-weight: 500;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	cursor: pointer;
}
.ot-label {
	flex: 1 1 180px;
	min-width: 140px;
}
.ot-color {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 4px 8px;
	border-radius: 6px;
	cursor: pointer;
	font-size: 12px;
	color: #5f6b7a;
}
.ot-color:hover {
	background: #f3f6fa;
}
.ot-swatch {
	width: 16px;
	height: 16px;
	border-radius: 4px;
	border: 1px solid #c4c4c4;
}
.ot-hex {
	font-family: 'Roboto Mono', ui-monospace, monospace;
}
.ot-actions {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 8px;
}
.ot-dirty {
	display: inline-flex;
	align-items: center;
	font-size: 13px;
	color: #b26a00;
}
</style>
